import { RefObject, useEffect } from 'react'
import { Platform } from 'react-native'
import {
  IQuarterProgress,
  useQuarterProgressReporter,
} from './useQuarterProgressReporter'

const VIMEO_ORIGIN = 'https://player.vimeo.com'
/** Reassinaturas dos eventos de progresso até o player responder. */
const SUBSCRIBE_RETRIES = 12
const SUBSCRIBE_RETRY_MS = 500
// Com `api=1` no embed o player atende pelo protocolo legado (froogaloop),
// cujo evento de progresso é `playProgress` e o de fim é `finish` — ele acka
// `addEventListener` de qualquer nome sem nunca emitir os que não conhece,
// então assinar só `timeupdate` falha em silêncio. Assina os dois protocolos
// e trata os dois formatos de payload.
const PROGRESS_EVENTS = ['timeupdate', 'playProgress']
// O último `timeupdate`/`playProgress` pode parar antes de 100%; o evento de
// fim garante o quarter 100 (na web é ele que marca a aula como concluída).
const END_EVENTS = ['ended', 'finish']

interface IUseVimeoIframeProgressParams {
  iframeRef: RefObject<HTMLIFrameElement>
  enabled: boolean
  onProgress: (params: IQuarterProgress) => void
  resetKey?: string | number
}

/**
 * Instrumenta o progresso de um player Vimeo embarcado via a JS API por
 * postMessage (sem depender do SDK). Assina o evento `timeupdate` do player e
 * reporta o progresso por quarter. Só ativa no web.
 */
export function useVimeoIframeProgress({
  iframeRef,
  enabled,
  onProgress,
  resetKey,
}: IUseVimeoIframeProgressParams) {
  const report = useQuarterProgressReporter(onProgress, resetKey)

  useEffect(() => {
    if (!enabled || Platform.OS !== 'web' || typeof window === 'undefined') {
      return
    }

    const post = (method: string, value?: string) => {
      const target = iframeRef.current?.contentWindow
      if (!target) return
      target.postMessage(JSON.stringify({ method, value }), VIMEO_ORIGIN)
    }

    const subscribe = () =>
      [...PROGRESS_EVENTS, ...END_EVENTS].forEach((eventName) =>
        post('addEventListener', eventName),
      )

    // O `ready` pode chegar antes deste efeito assinar, e o `load` do iframe
    // acontece antes do player existir — nos dois casos o pedido se perde e o
    // progresso nunca é reportado. Reassina até o primeiro evento de progresso
    // chegar; a chamada é idempotente do lado do Vimeo.
    let knownDuration = 0
    let retries = 0
    const retry = setInterval(() => {
      if (retries >= SUBSCRIBE_RETRIES) {
        clearInterval(retry)
        return
      }
      retries += 1
      subscribe()
    }, SUBSCRIBE_RETRY_MS)

    const handleMessage = (event: MessageEvent) => {
      if (event.origin !== VIMEO_ORIGIN) return
      let payload = event.data
      if (typeof payload === 'string') {
        try {
          payload = JSON.parse(payload)
        } catch {
          return
        }
      }
      if (!payload) return
      if (payload.event === 'ready') {
        subscribe()
        return
      }
      if (PROGRESS_EVENTS.includes(payload.event) && payload.data) {
        clearInterval(retry)
        // No protocolo legado seconds/duration chegam como string.
        const duration = Number(payload.data.duration)
        if (duration > 0) knownDuration = duration
        report(Number(payload.data.seconds), duration)
        return
      }
      if (END_EVENTS.includes(payload.event)) {
        const duration = Number(payload.data?.duration) || knownDuration
        report(duration, duration)
      }
    }

    window.addEventListener('message', handleMessage)
    // tenta assinar também no load, caso o evento ready já tenha passado
    const iframe = iframeRef.current
    iframe?.addEventListener('load', subscribe)
    subscribe()

    return () => {
      clearInterval(retry)
      window.removeEventListener('message', handleMessage)
      iframe?.removeEventListener('load', subscribe)
    }
  }, [enabled, report, iframeRef])
}
